import { Box, Heading, Image, Stack, Text } from "@vinomofo/components";
import { useEffect, useState } from "react";
import { RouteComponentProps, Router } from "@reach/router";

import { AccountSection } from "../AccountSection";
import useAuth from "../../../hooks/use-auth";
import {
  CopyIcon,
  RingIcon,
  GiftIcon,
  PopWineIcon,
  DeliveryIcon,
  UserIcon,
  DropletGreenVimIcon,
  DropletSilverVimIcon,
  DropletGoldVimIcon,
  DropletBlackVimIcon,
} from "@vinomofo/icons";
import greenBanner from "../../../images/loyalty/green-banner.svg";
import silverBanner from "../../../images/loyalty/silver-banner.svg";
import goldBanner from "../../../images/loyalty/gold-banner.svg";
import platinumBanner from "../../../images/loyalty/platinum-banner.svg";
import { AccountCard } from "../AccountCard";
import { COLORS } from "../../../styles";
import { formatNumber } from "../../../utils/string-helpers";
import useLoyaltyContext, {
  useLoyalty,
} from "../../../hooks/account/use-loyalty";
import moment from "moment";
import styled from "styled-components";
import {
  determineTierCouponValues,
  determineTierIndex,
  isUserAStaff,
} from "../../../utils/loyalty";
import { VinoUser } from "../../../contexts/auth/state";
import {
  Coupon,
  LoyaltyConfig,
  LoyaltyTier,
  LoyaltyTierLowerCase,
  TierAttainRetainReward,
  TierBanners,
  TierColors,
  TierPoints,
} from "../../../contexts/loyalty/types";
import React from "react";
import { Link } from "gatsby";

const Strong = styled.strong`
  opacity: 1;
`;
const Span = styled.span`
  opacity: 1;
`;
const StyledLink = styled(Link)`
  text-decoration: none;
  :hover {
    text-decoration: underline;
  }
`;

export const Rewards = (_props: RouteComponentProps): JSX.Element => {
  const { user } = useAuth();
  const { showLoyalty } = useLoyalty();
  const isStaff = isUserAStaff(user as VinoUser);
  return showLoyalty ? (
    <AccountSection heading="Welcome back, Very Important Mofo (VIM)">
      <Router basepath="/rewards">
        <MyRewards path="/" default />
      </Router>
    </AccountSection>
  ) : (
    <></>
  );
};

export const attainRewardV2: TierAttainRetainReward = {
  none: { icon: <DropletGreenVimIcon />, name: "GREEN" },
  green: { icon: <DropletSilverVimIcon />, name: "SILVER" },
  silver: { icon: <DropletGoldVimIcon />, name: "GOLD" },
  gold: { icon: <DropletBlackVimIcon />, name: "BLACK" },
  black: { icon: <DropletBlackVimIcon />, name: "FOUNDERS" },
  founders: null,
};

export const retainReward: TierAttainRetainReward = {
  none: { icon: <DropletGreenVimIcon />, name: "GREEN" },
  green: { icon: <DropletGreenVimIcon />, name: "GREEN" },
  silver: { icon: <DropletSilverVimIcon />, name: "SILVER" },
  gold: { icon: <DropletGoldVimIcon />, name: "GOLD" },
  black: { icon: <DropletBlackVimIcon />, name: "BLACK" },
  founders: { icon: <DropletBlackVimIcon />, name: "FOUNDERS" },
};

export const rewardIconsV2 = {
  none: <DropletGreenVimIcon />,
  green: <DropletGreenVimIcon />,
  silver: <DropletSilverVimIcon />,
  gold: <DropletGoldVimIcon />,
  black: <DropletBlackVimIcon />,
  founders: <DropletBlackVimIcon />,
};

export const tierColors: TierColors = {
  none: "tierGreen",
  green: "tierGreen",
  silver: "tierSilver",
  gold: "tierGold",
  black: "tierBlack",
};

export const tierDarkColors: TierColors = {
  none: "tierDarkGreen",
  green: "tierDarkGreen",
  silver: "tierDarkSilver",
  gold: "tierDarkGold",
  black: "tierDarkBlack",
};

export const attainTierColors: TierColors = {
  none: "mofoGreen",
  green: "tierSilver",
  silver: "tierGold",
  gold: "tierBlack",
  black: "tierBlack",
};
export const attainTierDarkColors: TierColors = {
  none: "mofoGreen",
  green: "tierDarkSilver",
  silver: "tierDarkGold",
  gold: "tierDarkBlack",
  black: "tierDarkBlack",
};

const MyRewards = (_props: RouteComponentProps): JSX.Element => {
  const { user, isLoading } = useAuth();
  const { loyalty, isLoading: loyaltyLoading, showLoyalty } = useLoyalty();
  const [tierName, setTierName] = useState("");
  const [revealBday, setRevealBday] = useState<boolean>(false);
  const [revealAnniv, setRevealAnniv] = useState<boolean>(false);
  const [annivCopied, setAnnivCopied] = useState<boolean>(false);
  const [bdayCopied, setBdayCopied] = useState<boolean>(false);
  const { findCoupon, config } = useLoyaltyContext();
  const [bdayCoupon, setBdayCoupon] = useState<Coupon | null>(null);
  const [annivCoupon, setAnnivCoupon] = useState<Coupon | null>(null);

  const handleFindCoupon = async (
    code: string,
    setter: (coupon: Coupon) => void
  ) => {
    const coupon = await findCoupon(code);
    if (coupon[0]) setter(coupon[0]);
  };

  useEffect(() => {
    handleFindCoupon(loyalty?.loyalty_birthday_coupon_code, setBdayCoupon);
    handleFindCoupon(loyalty?.loyalty_anniversary_coupon_code, setAnnivCoupon);
  }, [loyalty]);

  const attainPoints: TierPoints = {
    none: 0, // GREEN
    green: config?.loyaltyTierRangeValues?.SILVER_LOW_VALUE, // SILVER
    silver: config?.loyaltyTierRangeValues?.GOLD_LOW_VALUE, // GOLD
    gold: config?.loyaltyTierRangeValues?.BLACK_LOW_VALUE, // BLACK
    black: config?.loyaltyTierRangeValues?.FOUNDERS_LOW_VALUE, // FOUNDER
  };

  const retainPoints: TierPoints = {
    none: 0,
    green: 0,
    silver: config?.loyaltyTierRangeValues?.SILVER_RETAIN_VALUE,
    gold: config?.loyaltyTierRangeValues?.GOLD_RETAIN_VALUE,
    black: config?.loyaltyTierRangeValues?.BLACK_RETAIN_VALUE,
  };

  const banners: TierBanners = {
    green: greenBanner,
    none: greenBanner,
    silver: silverBanner,
    gold: goldBanner,
    black: platinumBanner,
  };

  const DetailsCard = ({ title, content, color, link, linkLabel }: any) => (
    <Box width={["100%", "100%", "48%"]} mt={[30, 30, 0]}>
      <Box
        style={{
          background: `linear-gradient(270deg, ${color}70 0%, ${color} 100%)`,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        }}
        color="white"
        padding={"5px 15px"}
        width="100%"
        borderRadius={10}
      >
        {title}
      </Box>
      <Box
        backgroundColor="white"
        color="black"
        padding={20}
        width="100%"
        borderRadius={10}
        style={{
          boxShadow: "5px 5px 15px 0px rgba(0, 0, 0, 0.15)",
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
        }}
      >
        <>{content}</>
        <br />
        <a href={link} style={{ textDecoration: "none" }}>
          <Text fontSize={"12px"} color="mofoGreen" textAlign="right">
            <Strong>{linkLabel}</Strong>
          </Text>
        </a>
      </Box>
    </Box>
  );

  const tierKey :LoyaltyTierLowerCase = loyalty?.loyalty_tier?.toLowerCase();

  const { birthdayValue, anniversaryValue } = determineTierCouponValues(
    config as LoyaltyConfig,
    tierKey?.toUpperCase()
  );

  // current earnings
  const _currentPoints: string | number =
    loyalty?.loyalty_current_points || "0";
  const currentPoints = formatNumber(_currentPoints) || "0";

  let stage = loyalty?.loyalty_tier_stage;
  if (tierKey === "none" || tierKey === "green") stage = "attain";

  // attain
  const _pointsToAttain = attainPoints[tierKey] || 0;
  const pointsToAttain = formatNumber(_pointsToAttain);

  // retain
  const _pointsToRetain: number = retainPoints[tierKey];
  const pointsToRetain: string = formatNumber(retainPoints[tierKey]);
  const _pointsToEarnToRetain: number = _pointsToRetain - _currentPoints;
  const _pointsToEarnToAttain: number = _pointsToAttain - _currentPoints;
  let pointsToEarnToRetain: string = formatNumber(
    _pointsToRetain - _currentPoints
  );
  let pointsToEarnToAttain: string = formatNumber(
    _pointsToAttain - _currentPoints
  );
  if (_pointsToEarnToRetain <= 0) pointsToEarnToRetain = 0;
  if (_pointsToEarnToAttain <= 0) pointsToEarnToAttain = 0;

  // UI position in percentage
  let sliderLocation =
    (_currentPoints /
      (stage === "retain" ? _pointsToRetain : _pointsToAttain)) *
    100;

  sliderLocation = sliderLocation > 100 ? 100 : sliderLocation;

  const noAttain: boolean =
    tierKey === "green" || tierKey === "black" || stage === "attain";
  const noRetain: boolean = tierKey === "green" || tierKey === "none";

  const isAttaining =
    loyalty?.loyalty_tier_stage == "attain" && tierKey !== "black";

  const isBdayCouponEnabled: boolean = bdayCoupon?.status === "ENABLED";
  const isBdayCouponUsed: boolean = bdayCoupon?.current_uses > 0 || false;
  const isBdayExpired: boolean =
    (!isBdayCouponUsed && moment(bdayCoupon?.end_date).isBefore(moment())) ||
    false;

  const isAnnivCouponEnabled: boolean = annivCoupon?.status === "ENABLED";
  const isAnnivCouponUsed: boolean = annivCoupon?.current_uses > 0 || false;
  const isAnnivExpired: boolean =
    (!isAnnivCouponUsed && moment(annivCoupon?.end_date).isBefore(moment())) ||
    false;

  const accountCreatedMoment = moment(loyalty?.vm_account_created_date);
  let years = moment().year() - accountCreatedMoment.year();
  if (moment().month() > accountCreatedMoment.month()) {
    years++;
  } else if (moment().month() === accountCreatedMoment.month()) {
    if (moment().date() > accountCreatedMoment.date()) {
      years++;
    }
  }
// Removed commented-out code for clarity and to reduce clutter.

  const [rightPosition, setRightPosition] = useState("0px");

  const staffDetailedCardContent = <Box
    display={["flex", "flex"]}
    gridGap={1}
    flexDirection="column"
    margin-top="-100px"
  >
    <Box
      display="flex"
      justifyContent="space-between"
      width="100%"
      alignItems="center"
    >
      <Box width={65}>
        <PopWineIcon size="38px" />
      </Box>
      <Box
        display="flex"
        justifyContent="space-between"
        width="100%"
        alignItems="center"
        mt="5px"
      >
        <Text fontSize={"12px"}>
          Exclusive Wines & Discounts
        </Text>
        <Text fontSize={"12px"}>{``}</Text>
      </Box>
    </Box>
  </Box>
  useEffect(() => {
    if (user && isUserAStaff(user)) {
      setTierName(user?.customer_group?.name);
    } else {
      setTierName(loyalty?.loyalty_tier);
    }
  }, [user, loyalty]);

  useEffect(() => {
    const handleResize = () => {
      const windowWidth = window.innerWidth;
      // Decrease the right position incrementally as screen size decreases
      if (windowWidth < 767) {
        // mobile view
        const newRightPosition = (windowWidth - 767) / 14;
        setRightPosition(`${newRightPosition}px`);
      } else if (windowWidth >= 980 && windowWidth < 1200) {
        // tablet view
        const newRightPosition = (windowWidth - 1200) / 14;
        setRightPosition(`${newRightPosition}px`);
      } else {
        setRightPosition("0");
      }
    };

    // Set the position initially
    handleResize();

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return !isLoading && !loyaltyLoading ? (
    <Stack space={3}>
      <AccountCard>
        <Box padding={["20px", "20px", "40px"]}>
          <Box
            position="relative"
            height={100}
            overflow="hidden"
            borderRadius={10}
            style={{ borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}
          >
            {user && (
              <Box
                width={["150%", "120%", "100%"]}
                style={{ position: "absolute", left: 0, top: 0, zIndex: 0 }}
              >
                <Image src={banners[tierKey || "none"]} width={"100%"} />
              </Box>
            )}
            <Box
              display={["flex", "flex"]}
              justifyContent="space-between"
              zIndex={1}
              position="absolute"
              bottom={0}
              padding={20}
              width="100%"
              alignItems="flex-end"
            >
              <Heading as="h5" variant="xxs" color="white">
                {user?.name}
              </Heading>
              <Box>
                <Text
                  color="white"
                  fontWeight={700}
                  lineHeight={"70px"}
                  textAlign={"right"}
                >
                  {tierName}
                </Text>
                <Text color="white" lineHeight={1}>
                  POINTS <Strong>{currentPoints}</Strong>
                </Text>
              </Box>
            </Box>
          </Box>
          <Box padding={"20px 5px"}>
            <Box
              display={["flex", "flex"]}
              justifyContent="space-between"
              width="100%"
              alignItems="flex-end"
            >
              <Text color="black">THIS MEMBERSHIP CYCLE</Text>
              <Box display={["none", "block", "block"]}>
                <Text fontSize={1} color="black">
                  MEMBER SINCE{" "}
                  {moment(loyalty?.vm_account_created_date).format(
                    "DD MMM YYYY"
                  )}
                </Text>
              </Box>
            </Box>
            <Box width="100%" alignItems="flex-end">
              <Heading as="p" variant="xxs" color="BLACK">
                You’ve earned <Strong>{currentPoints} points</Strong> this year
              </Heading>
              {parseFloat(currentPoints) > 0 && (
                <Heading as="h5" variant="xxs" color="BLACK">
                  GOOD JOB!
                </Heading>
              )}
            </Box>
            <Box display={["block", "none", "none"]} mb={30} mt={20}>
              <Text fontSize={1} color="black">
                MEMBER SINCE{" "}
                {moment(loyalty?.vm_account_created_date).format("DD MMM YYYY")}
              </Text>
            </Box>
          </Box>
          {(tierKey === "black" && stage === "attain") || tierKey === "none" || tierName === "STAFF" ? (
            <Box padding={20} />
          ) : (
            <Box
              width="100%"
              padding={2}
              mb={3}
              justifyContent="center"
              alignItems="center"
              display="flex"
            >
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                justifyContent="center"
                position="relative"
                mt={[40, 60, 80]}
                mb={[10, 10, 50]}
                width="80%"
                marginLeft={noAttain ? 0 : -80}
              >
                <Box
                  style={{
                    height: 1,
                    position: "absolute",
                    left: 10,
                    right: noAttain ? 40 : -70,
                    background: `linear-gradient(90deg, #0000 0%, #000000 50%, #0000 100%)`,
                  }}
                />
                <Box
                  style={{
                    height: 20,
                    width: 20,
                    border: "3px solid black",
                    borderRadius: 20,
                    position: "absolute",
                    left: 0,
                  }}
                />

                <You
                  position={sliderLocation > 60 ? "bottom" : "top"}
                  sliderLocation={sliderLocation}
                  bgColor={COLORS[tierDarkColors[tierKey || "none"]]}
                  currentPoints={currentPoints}
                />

                <Retain
                  stage={stage}
                  tierName={
                    stage === "attain"
                      ? attainRewardV2[tierKey]?.name
                      : tierKey?.toUpperCase()
                  }
                  points={stage === "attain" ? pointsToAttain : pointsToRetain}
                  icon={
                    stage === "attain"
                      ? attainRewardV2[tierKey]?.icon
                      : retainReward[tierKey]?.icon
                  }
                  rightPosition={rightPosition}
                />

                {!noAttain && (
                  <Attain
                    tierName={attainRewardV2[tierKey]?.name}
                    icon={attainRewardV2[tierKey]?.icon}
                    points={pointsToAttain}
                  />
                )}
              </Box>
            </Box>
          )}
          <Box
            display={["flex", "flex"]}
            justifyContent="space-between"
            width="100%"
            alignItems="flex-start"
            flexDirection={["column", "column", "row"]}
          >
            <DetailsCard
              title={
                tierName !== "STAFF" ? <Text fontSize={1} color="white" textAlign="left">
                  {isAttaining || noRetain ? "REACHING" : "KEEPING"}{" "}
                  <Strong>VIM</Strong>
                  {isAttaining
                    ? attainRewardV2[tierKey]?.name
                    : retainReward[tierKey]?.name}
                </Text> 
                : 
                <Text fontSize={1} color="white" textAlign="left">
                  KEEPING VIMSTAFF
                </Text>
              }
              color={COLORS[tierDarkColors[tierKey || "none"]]}
              link={tierKey === "none" ? "#" : "/account/order-history"}
              linkLabel={
                tierKey === "none" ? null : "See your purchase history here >"
              }
              content={
                tierName !== "STAFF" ? <Box display={["flex", "flex"]} gridGap={3}>
                  {rewardIconsV2[tierKey || "none"]
                    ? React.cloneElement(rewardIconsV2[tierKey || "none"], {
                        size: "50px",
                      })
                    : null}

                  {tierKey === "none" || currentPoints === "0" ? (
                    <Text fontSize={1}>
                      Make your first purchase to start your loyalty journey!
                    </Text>
                  ) : tierKey === "black" && _pointsToEarnToRetain <= 0 ? (
                    <Text fontSize={1}>
                      You've earned enough to retain <Strong>VIM</Strong>
                      {isAttaining
                        ? attainRewardV2[tierKey]?.name
                        : retainReward[tierKey]?.name}
                    </Text>
                  ) : _pointsToEarnToAttain <= 0 ? (
                    <Text fontSize={1}>
                      You have {isAttaining ? "reached" : "kept"}{" "}
                      <Strong>VIM</Strong>
                      {isAttaining
                        ? attainRewardV2[tierKey]?.name
                        : retainReward[tierKey]?.name}
                    </Text>
                  ) : (
                    <Text fontSize={1}>
                      You need to earn{" "}
                      <Strong>
                        {isAttaining
                          ? pointsToEarnToAttain || 0
                          : pointsToEarnToRetain || 0}{" "}
                        points
                      </Strong>{" "}
                      before{" "}
                      <Strong>
                        {moment(loyalty?.vm_account_created_date)
                          .add(years, "y")
                          .format("DD MMM YYYY")}
                        {/* {moment().add(years, "y").format("YYYY")} */}
                      </Strong>{" "}
                      to {isAttaining ? "reach" : "keep"} <Strong>VIM</Strong>
                      {isAttaining
                        ? attainRewardV2[tierKey]?.name
                        : retainReward[tierKey]?.name}
                    </Text>
                  )}
                </Box>
                  :
                  <Box display={["flex", "flex"]} gridGap={3}>
                    <Text fontSize={1}>
                      Enjoy the view from the mountaintop. Sit back, relax and may be look at our amazing collection.
                    </Text>
                  </Box>
              }
            />
            {tierKey === "noneX" ? null : (
              <DetailsCard
                title={
                  tierName !== "STAFF" ? <Text fontSize={1} color="white" textAlign="left">
                    {tierKey === "none" ? null : "YOUR"} <Strong>VIM</Strong>
                    {tierKey === "none" ? "GREEN" : tierKey?.toUpperCase()}{" "}
                    BENEFITS
                  </Text>
                  : <Text fontSize={1} color="white" textAlign="left">YOUR VIMSTAFF BENEFITS</Text>

                }
                color={COLORS[tierDarkColors[tierKey || "none"]]}
                link="/loyalty"
                linkLabel="See more about the benefits >"
                content={
                  tierName === "STAFF" ? staffDetailedCardContent : <Box
                    display={["flex", "flex"]}
                    gridGap={1}
                    flexDirection="column"
                    margin-top="-100px"
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      width="100%"
                      alignItems="center"
                    >
                      <Box width={65}>
                        <DeliveryIcon size="37px" />
                      </Box>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        width="100%"
                        alignItems="bottom"
                        mt="8px"
                      >
                        <Text fontSize={"12px"}>
                          {tierKey === "gold" || tierKey === "black"
                            ? "Free Shipping"
                            : "Free Shipping with order of 3+ cases"}
                        </Text>
                        <Text fontSize={"12px"}>{`       `}</Text>
                      </Box>
                    </Box>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      width="100%"
                      alignItems="center"
                    >
                      <Box width={65}>
                        <PopWineIcon size="38px" />
                      </Box>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        width="100%"
                        alignItems="center"
                        mt="5px"
                      >
                        <StyledLink to={"/events/very-important-deals"}>
                          <Text fontSize={"12px"}>
                            Exclusive Wines & Discounts
                          </Text>
                        </StyledLink>

                        <Text fontSize={"12px"}>{``}</Text>
                      </Box>
                    </Box>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      width="100%"
                      alignItems="center"
                    >
                      <Box width={60}>
                        <GiftIcon size="44px" />
                      </Box>
                      {tierKey === "none" ? (
                        <Text fontSize={"12px"}>
                          Make your first purchase to unlock your birthday
                          benefit
                        </Text>
                      ) : (
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          width="100%"
                          alignItems={["center", "center", "left", "center"]}
                          flexDirection={["row", "row", "column", "row"]}
                          mt="6px"
                        >
                          <Box width={["60%", "60%", "100%", "60%"]}>
                            <Text fontSize={"12px"}>
                              Your ${birthdayValue} Birthday Gift
                            </Text>
                          </Box>

                          {bdayCoupon &&
                          loyalty?.loyalty_birthday_coupon_code ? (
                            <Box style={{ cursor: "pointer" }}>
                              <Text
                                fontSize={"10px"}
                                textAlign="right"
                                color={bdayCopied ? "mofoGreen" : "black"}
                              >
                                {!revealBday ? (
                                  <>
                                    {!isBdayExpired && isBdayCouponEnabled ? (
                                      <>
                                        {isBdayCouponUsed ? (
                                          <Box
                                            display="flex"
                                            flexDirection="row"
                                          >
                                            <Span
                                              onClick={() => {
                                                if (isBdayCouponEnabled)
                                                  setRevealBday(!revealBday);
                                              }}
                                            >
                                              {bdayCopied ? "Copied!" : "USED"}
                                            </Span>
                                            <Copy
                                              value={
                                                loyalty?.loyalty_birthday_coupon_code
                                              }
                                              setCopy={setBdayCopied}
                                            />
                                          </Box>
                                        ) : (
                                          <Box
                                            display="flex"
                                            flexDirection="row"
                                          >
                                            <Span
                                              onClick={() => {
                                                if (isBdayCouponEnabled)
                                                  setRevealBday(!revealBday);
                                              }}
                                            >
                                              {bdayCopied
                                                ? "Copied!"
                                                : "USE NOW"}
                                            </Span>
                                            <Copy
                                              value={
                                                loyalty?.loyalty_birthday_coupon_code
                                              }
                                              setCopy={setBdayCopied}
                                            />
                                          </Box>
                                        )}
                                      </>
                                    ) : isBdayExpired ? (
                                      <Box display="flex" flexDirection="row">
                                        <Span
                                          onClick={() => {
                                            if (isBdayCouponEnabled)
                                              setRevealBday(!revealBday);
                                          }}
                                        >
                                          {bdayCopied ? "Copied!" : "EXPIRED"}
                                        </Span>
                                        <Copy
                                          value={
                                            loyalty?.loyalty_birthday_coupon_code
                                          }
                                          setCopy={setBdayCopied}
                                        />
                                      </Box>
                                    ) : (
                                      "TBC"
                                    )}
                                  </>
                                ) : (
                                  <Box display="flex" flexDirection="row">
                                    <Span
                                      onClick={() => {
                                        if (isBdayCouponEnabled)
                                          setRevealBday(!revealBday);
                                      }}
                                    >
                                      {bdayCopied
                                        ? "Copied!"
                                        : loyalty?.loyalty_birthday_coupon_code}
                                    </Span>
                                    <Copy
                                      value={
                                        loyalty?.loyalty_birthday_coupon_code
                                      }
                                      setCopy={setBdayCopied}
                                    />
                                  </Box>
                                )}
                              </Text>
                            </Box>
                          ) : (
                            <Text
                              fontSize={"10px"}
                              textAlign="right"
                              color={annivCopied ? "mofoGreen" : "black"}
                            >
                              TBC
                            </Text>
                          )}
                        </Box>
                      )}
                    </Box>
                    {tierKey === "green" || tierKey === "none" ? null : (
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        width="100%"
                        alignItems="center"
                        mt={[0, 0, "5px"]}
                      >
                        <Box width={60}>
                          <RingIcon size="43px" />
                        </Box>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          width="100%"
                          alignItems={["center", "center", "left", "center"]}
                          flexDirection={["row", "row", "column", "row"]}
                        >
                          <Box width={["60%", "60%", "100%", "60%"]}>
                            <Text fontSize={"12px"}>
                              Your ${anniversaryValue} Anniversary Voucher
                            </Text>
                          </Box>

                          {annivCoupon &&
                          loyalty?.loyalty_anniversary_coupon_code ? (
                            <Box style={{ cursor: "pointer" }}>
                              <Text
                                fontSize={"10px"}
                                textAlign="right"
                                color={annivCopied ? "mofoGreen" : "black"}
                              >
                                {!revealAnniv ? (
                                  <>
                                    {!isAnnivExpired && isAnnivCouponEnabled ? (
                                      <>
                                        {isAnnivCouponUsed ? (
                                          <Box
                                            display="flex"
                                            flexDirection="row"
                                          >
                                            <Span
                                              onClick={() => {
                                                if (isAnnivCouponEnabled)
                                                  setRevealAnniv(!revealAnniv);
                                              }}
                                            >
                                              {annivCopied ? "Copied!" : "USED"}
                                            </Span>
                                            <Copy
                                              value={
                                                loyalty?.loyalty_anniversary_coupon_code
                                              }
                                              setCopy={setAnnivCopied}
                                            />
                                          </Box>
                                        ) : (
                                          <Box
                                            display="flex"
                                            flexDirection="row"
                                          >
                                            {" "}
                                            <Span
                                              onClick={() => {
                                                if (isAnnivCouponEnabled)
                                                  setRevealAnniv(!revealAnniv);
                                              }}
                                            >
                                              {annivCopied
                                                ? "Copied!"
                                                : "USE NOW"}
                                            </Span>
                                            <Copy
                                              value={
                                                loyalty?.loyalty_anniversary_coupon_code
                                              }
                                              setCopy={setAnnivCopied}
                                            />
                                          </Box>
                                        )}
                                      </>
                                    ) : isAnnivExpired ? (
                                      <Box display="flex" flexDirection="row">
                                        <Span
                                          onClick={() => {
                                            if (isAnnivCouponEnabled)
                                              setRevealAnniv(!revealAnniv);
                                          }}
                                        >
                                          {annivCopied ? "Copied!" : "EXPIRED"}
                                        </Span>
                                        <Copy
                                          value={
                                            loyalty?.loyalty_anniversary_coupon_code
                                          }
                                          setCopy={setAnnivCopied}
                                        />
                                      </Box>
                                    ) : (
                                      "TBC"
                                    )}
                                  </>
                                ) : (
                                  <Box display="flex" flexDirection="row">
                                    <Span
                                      onClick={() =>
                                        setRevealAnniv(!revealAnniv)
                                      }
                                    >
                                      {annivCopied
                                        ? "Copied!"
                                        : loyalty?.loyalty_anniversary_coupon_code}
                                    </Span>
                                    <Copy
                                      value={
                                        loyalty?.loyalty_anniversary_coupon_code
                                      }
                                      setCopy={setAnnivCopied}
                                    />
                                  </Box>
                                )}
                              </Text>
                            </Box>
                          ) : (
                            <Text
                              fontSize={"10px"}
                              textAlign="right"
                              color={annivCopied ? "mofoGreen" : "black"}
                            >
                              TBC
                            </Text>
                          )}
                        </Box>
                      </Box>
                    )}
                  </Box>
                }
              />
            )}
          </Box>
        </Box>
      </AccountCard>
    </Stack>
  ) : (
    <></>
  );
};
interface CopyProps {
  value: any;
  setCopy: any;
}

const Copy = ({ value, setCopy }: CopyProps) => (
  <Span
    onClick={() => {
      navigator.clipboard.writeText(value);
      setCopy(true);
      const to = setTimeout(() => {
        setCopy(false);
        clearTimeout(to);
      }, 1000);
    }}
  >
    <Box display={["none", "none", "block"]} ml="5px">
      <CopyIcon />
    </Box>
    <Box display={["block", "block", "none"]} ml="8px">
      <CopyIcon />
    </Box>
  </Span>
);

interface RetainAttainProps {
  stage?: string;
  tierName: string;
  points: number;
  icon: string;
  rightPosition: string;
}

const Attain = ({ tierName, points = 0, icon }: RetainAttainProps) => {
  return (
    <Box
      style={{
        width: 80,
        position: "absolute",
        bottom: -8,
        right: -100,
      }}
      display="flex"
      flexDirection="column"
      alignItems="center"
    >
      <Text
        fontSize={["10px", "12px", "12px"]}
        color="black"
        textAlign="center"
      >
        <Strong>KEEP</Strong> <br /> <Strong>VIM</Strong>
        {tierName}
      </Text>
      <Text fontSize={["10px", "12px", "12px"]}>{points} points</Text>
      <Box style={{ height: 10 }} />

      {icon
        ? React.cloneElement(icon, {
            size: "20px",
          })
        : null}
    </Box>
  );
};

const Retain = ({
  stage,
  tierName,
  points = 0,
  icon,
  rightPosition,
}: RetainAttainProps) => {
  return (
    <Box
      style={{
        width: 80,
        position: "absolute",
        bottom: -15,
        right: rightPosition,
      }}
      display="flex"
      flexDirection="column"
      alignItems="center"
    >
      <Text
        fontSize={["10px", "12px", "12px"]}
        color="black"
        textAlign="center"
      >
        <Strong>{stage === "attain" ? "REACH" : "KEEP"}</Strong> <br />{" "}
        <Strong>VIM</Strong>
        {tierName}
      </Text>
      {points > 0 && (
        <Text fontSize={["10px", "12px", "12px"]}>{points} points</Text>
      )}
      <Box height={["7px", "10px"]} />

      {icon
        ? React.cloneElement(icon, {
            size: "30px",
          })
        : null}
    </Box>
  );
};
interface YouProps {
  position: string;
  sliderLocation: number;
  bgColor: string;
  currentPoints: string;
}
const You = ({
  position = "top",
  sliderLocation,
  bgColor,
  currentPoints,
}: YouProps) => {
  let styles: any = {
    width: 80,
    position: "absolute",
    left: `${sliderLocation}%`,
  };
  if (position == "bottom") styles.top = "calc(100% - 15px)";
  if (position == "top") styles.bottom = "calc(100% - 15px)";
  return (
    <Box
      style={{
        position: "relative",
        marginLeft: -115,
        width: "80%",
        zIndex: 1,
      }}
    >
      <Box
        style={styles}
        display="flex"
        flexDirection="column"
        alignItems="center"
      >
        {position === "bottom" && (
          <>
            <Box
              style={{
                height: 30,
                width: 30,
                backgroundColor: bgColor,
                borderRadius: 30,
                padding: "3px 5px",
              }}
            >
              <UserIcon color="white" size="20px" />
            </Box>

            <Box style={{ height: 10 }} />
          </>
        )}
        <Heading
          as="h5"
          variant="xxs"
          color="BLACK"
          fontSize={["10px", "12px", "12px"]}
        >
          YOU
        </Heading>
        <Text fontSize={["10px", "12px", "12px"]}>{currentPoints} points</Text>
        {position === "top" && (
          <>
            <Box style={{ height: 10 }} />
            <Box
              style={{
                height: 30,
                width: 30,
                backgroundColor: bgColor,
                borderRadius: 30,
                padding: "3px 5px",
              }}
            >
              <UserIcon color="white" size="20px" />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};
